import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import 'moment/locale/pt-br';
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import {makeStyles} from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import {SaveOutlined} from '@material-ui/icons';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import TextButton from '../../../shared/buttons/TextButton.tsx';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import Message from '../../../shared/message/Message.js';
import {CpfNumberFormatCustom} from '../../../shared/formatters/index.js';
import {CampoData, CampoTexto} from '../../components/processo/CamposProcesso.jsx';
import {
    FORMATO_DATA_WIRE,
    cpfDuplicado,
    cpfValido,
    dataFutura,
    dataWireValida,
    mensagemCpfDuplicado,
    normalizarCpf
} from '../../components/processo/processoSelecao.js';

const TAMANHO_MAXIMO_NOME = 100;
const TAMANHO_MAXIMO_CODIGO = 30;

const FormSchema = Yup.object().shape({
    nome: Yup.string().trim()
        .required('É obrigatório')
        .max(TAMANHO_MAXIMO_NOME, `Máximo de ${TAMANHO_MAXIMO_NOME} caracteres`),
    cpf: Yup.string()
        .test('obrigatorio', 'É obrigatório', valor => !!normalizarCpf(valor))
        .test('valido', 'CPF inválido', valor => !normalizarCpf(valor) || cpfValido(valor)),
    codigoInscrito: Yup.string().max(TAMANHO_MAXIMO_CODIGO, `Máximo de ${TAMANHO_MAXIMO_CODIGO} caracteres`),
    dataNascimento: Yup.string()
        .test('valida', 'Data inválida', valor => !valor || dataWireValida(valor, FORMATO_DATA_WIRE))
        .test('futura', 'Data de nascimento não pode ser uma data futura', valor => !dataFutura(valor))
});

const VALORES_VAZIOS = {nome: '', cpf: '', codigoInscrito: '', dataNascimento: ''};

const useStyles = makeStyles(theme => ({
    paper: {
        width: 800,
        maxWidth: '100%'
    },
    contentRoot: {
        overflowY: 'initial'
    },
    mensagem: {
        marginBottom: theme.spacing(2)
    }
}));

/*
 * Candidato adicionado à mão na carga do processo de seleção
 * (_formAdicionarCarga do PrimeFaces): nome, CPF (com dígitos verificadores),
 * código do inscrito e data de nascimento (não futura). CPF já presente na
 * carga é recusado aqui mesmo, com o diálogo aberto.
 */
export default function DialogCargaCandidato({open, onClose, cargas = [], onSalvar}) {
    const classes = useStyles();
    const [mensagem, setMensagem] = useState('');
    const {control, handleSubmit, reset, formState: {errors, isSubmitting}} = useForm({
        resolver: yupResolver(FormSchema),
        defaultValues: VALORES_VAZIOS
    });

    const preencher = () => {
        reset(VALORES_VAZIOS);
        setMensagem('');
    };

    useEffect(() => {
        if (open) {
            preencher();
        }
    }, [open]);

    const submit = valores => {
        const cpf = normalizarCpf(valores.cpf);
        if (cpfDuplicado(cargas, cpf)) {
            setMensagem(mensagemCpfDuplicado(cpf));
            return;
        }
        onSalvar({
            id: null,
            nome: valores.nome.trim(),
            cpf,
            codigoInscrito: valores.codigoInscrito ? valores.codigoInscrito.trim() : null,
            dataNascimento: valores.dataNascimento || null
        });
        onClose();
    };

    /* O diálogo fica dentro do form da página na árvore do React: o submit não pode subir até ele. */
    const aoEnviar = evento => {
        evento.stopPropagation();
        handleSubmit(submit)(evento);
    };

    const props = {control, errors};

    return (
        <Dialog open={open} onClose={onClose} TransitionProps={{onEntering: preencher}} scroll='body'
                aria-labelledby='dialog-carga-candidato-titulo' classes={{paper: classes.paper}}>
            <DialogTitle id='dialog-carga-candidato-titulo'>Adicionar candidato à carga</DialogTitle>
            <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
                <form noValidate autoComplete='off' onSubmit={aoEnviar}>
                    <DialogContent classes={{root: classes.contentRoot}}>
                        {!!mensagem && (
                            <div className={classes.mensagem}>
                                <Message type='error' variant='standard'>{mensagem}</Message>
                            </div>
                        )}
                        <Grid container spacing={3}>
                            <CampoTexto {...props} nome='nome' rotulo='Nome *' sm={12} maxLength={TAMANHO_MAXIMO_NOME}/>
                            <Grid item xs={12} sm={6}>
                                <LabelValue label='CPF *'>
                                    <FormControl variant='outlined' fullWidth error={!!errors.cpf}>
                                        <Controller
                                            name='cpf'
                                            control={control}
                                            render={({field}) => (
                                                <OutlinedInput
                                                    name={field.name}
                                                    value={field.value ?? ''}
                                                    onChange={field.onChange}
                                                    onBlur={field.onBlur}
                                                    inputRef={field.ref}
                                                    inputComponent={CpfNumberFormatCustom}/>
                                            )}/>
                                        {!!errors.cpf && <FormHelperText error>{errors.cpf.message}</FormHelperText>}
                                    </FormControl>
                                </LabelValue>
                            </Grid>
                            <CampoTexto {...props} nome='codigoInscrito' rotulo='Cód. Inscrito' maxLength={TAMANHO_MAXIMO_CODIGO}/>
                            <CampoData {...props} nome='dataNascimento' rotulo='Data de nascimento' disableFuture/>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <TextButton onClick={onClose}>Fechar</TextButton>
                        <ContainedButton type='submit' startIcon={<SaveOutlined/>} disabled={isSubmitting}>Salvar</ContainedButton>
                    </DialogActions>
                </form>
            </MuiPickersUtilsProvider>
        </Dialog>
    );
}

DialogCargaCandidato.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    /* Candidatos já na carga (para recusar CPF repetido). */
    cargas: PropTypes.array,
    /* Recebe {id: null, nome, cpf (só dígitos), codigoInscrito, dataNascimento (YYYY-MM-DD)}. */
    onSalvar: PropTypes.func.isRequired
};
